/**
 * Auth API Service
 * Handles authentication-related API calls
 */

import { apiClient } from './client';
import type {
  User,
  LoginRequest,
  LoginResponse,
  RequestAccessRequest,
  RequestAccessResponse,
  MagicLinkVerifyRequest,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from '@/types/api';
import type { UpdateProfileRequest, SetPasswordRequest } from '@/types/profile';

/**
 * Auth response with computed expiresAt field
 */
export interface AuthResponseWithExpiry extends LoginResponse {
  /** Computed ISO 8601 expiration timestamp */
  expiresAt: string;
}

/**
 * Convert expiresIn (seconds) to expiresAt (ISO 8601 string)
 */
function computeExpiresAt(expiresIn: number): string {
  return new Date(Date.now() + expiresIn * 1000).toISOString();
}

/**
 * Auth API service
 */
export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginRequest): Promise<AuthResponseWithExpiry> {
    const response = await apiClient.post<LoginResponse>('/api/Auth/login', credentials);
    return {
      ...response,
      expiresAt: computeExpiresAt(response.expiresIn),
    };
  },

  /**
   * Request access (public registration)
   */
  async requestAccess(request: RequestAccessRequest): Promise<RequestAccessResponse> {
    return apiClient.post<RequestAccessResponse>('/api/Auth/request-access', request);
  },

  /**
   * Verify a magic link token and authenticate
   */
  async verifyMagicLink(request: MagicLinkVerifyRequest): Promise<AuthResponseWithExpiry> {
    const response = await apiClient.post<LoginResponse>('/api/Auth/verify-magic-link', request);
    return {
      ...response,
      expiresAt: computeExpiresAt(response.expiresIn),
    };
  },

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    return apiClient.post<void>('/api/Auth/logout');
  },

  /**
   * Get the current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return apiClient.get<User>('/api/Auth/me');
  },

  /**
   * Request a password reset email
   */
  async requestPasswordReset(request: RequestPasswordResetRequest): Promise<RequestPasswordResetResponse> {
    return apiClient.post<RequestPasswordResetResponse>('/api/Auth/request-password-reset', request);
  },

  /**
   * Reset password with token
   */
  async resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    return apiClient.post<ResetPasswordResponse>('/api/Auth/reset-password', request);
  },

  /**
   * Update the current user's profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    return apiClient.put<User>('/api/Auth/profile', data);
  },

  /**
   * Set a new password (authenticated)
   */
  async setPassword(data: SetPasswordRequest): Promise<void> {
    return apiClient.post<void>('/api/Auth/set-password', data);
  },

  /**
   * Send email verification link
   */
  async sendEmailVerification(): Promise<void> {
    return apiClient.post<void>('/api/Auth/profile/send-verification');
  },

  /**
   * Request an email address change
   */
  async requestEmailChange(newEmail: string): Promise<void> {
    return apiClient.post<void>('/api/Auth/profile/request-email-change', { newEmail });
  },
};
